import t from 'react-translate';
import ClickableContainer from 'components/clickable-container';
import ProfilePicture from 'org_level_profile/components/profile-picture';
import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { MentoringProgramEnrollment } from 'redux/schemas/models/mentoring-program-enrollments';
import NvTooltip from 'shared/components/nv-tooltip';
import NvIcon from 'shared/components/nv-icon';
import { doubleModalZIndex } from 'shared/components/nv-modal';
import { css } from '@emotion/react';
import { novoEdBlue } from 'athena/styles/colors';
import { white } from 'styles/global_defaults/colors';

type SwitchAssigneeOptionProps = {
  itemId: number;
  assignedTo: number;
  enrollments: { [id: number]: MentoringProgramEnrollment };
  dataQATag: string;
  isReadOnly?: boolean;
  completedTooltip?: string;
};

const styles = css`
  .switch-assignee-icon {
    display: none !important;
  }

  &:hover {
    .switch-assignee-icon {
      background: ${novoEdBlue};
      color: ${white};
      border-radius: 50%;
      padding: 4px;
      display: inline !important;
    }
    div {
      display: none !important;
    }
  }
`;

const SwitchAssigneeOption = ({
  itemId,
  assignedTo,
  enrollments,
  dataQATag,
  isReadOnly = false,
  completedTooltip,
}: SwitchAssigneeOptionProps) => {
  const { setValue } = useFormContext();
  const [currentAssignee, setCurrentAssignee] = useState(enrollments[assignedTo]);
  const { id, user } = currentAssignee || {};
  const connectionEnrollmentId = Object.keys(enrollments).find(
    (enrollmentId) => Number(enrollmentId) !== id,
  );
  const connectionEnrollment: MentoringProgramEnrollment = enrollments[connectionEnrollmentId];

  const handleSwitchAssignee = () => {
    setCurrentAssignee(connectionEnrollment);
    setValue('assignedTo', Number(connectionEnrollmentId));
  };

  useEffect(() => {
    setCurrentAssignee(enrollments[assignedTo]);
  }, [assignedTo]);

  if (completedTooltip) {
    return user ? (
      <NvTooltip placement='top' text={completedTooltip} zIndex={doubleModalZIndex}>
        <div>
          <ProfilePicture profile={user} size={24} />
        </div>
      </NvTooltip>
    ) : null;
  }

  if (isReadOnly) {
    return user ? <ProfilePicture profile={user} size={24} /> : null;
  }

  if (!connectionEnrollment) return null;

  const changeOwnerLabel = t.MENTORING_PROGRAMS.PARTICIPANT_HOME.COMMON.TOOLTIP.CHANGE_OWNER(
    connectionEnrollment.user.fullName,
  );

  return currentAssignee ? (
    <ClickableContainer
      css={styles}
      onClick={handleSwitchAssignee}
      data-qa={dataQATag}
      data-qa-id={`${dataQATag}-${itemId}`}
      aria-label={changeOwnerLabel}
    >
      <NvTooltip placement='top' text={changeOwnerLabel} zIndex={doubleModalZIndex}>
        <NvIcon icon='switch' size='smallest' className='switch-assignee-icon' />
      </NvTooltip>
      <ProfilePicture profile={user} size={24} />
    </ClickableContainer>
  ) : null;
};

export default SwitchAssigneeOption;
